import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { faBuilding, faChevronLeft, faList } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { B2Button } from "@hdb/biome-2";
import { ZoneTypeId } from "src/core/enums/zone-enums";
import type { FloorShape } from "src/shared/models/floorPlan";
import type { Zone, ZoneAsset } from "../../components/types";
import StickyNoteComponent from "src/modules/dashboard/components/dashboard-floor-plan/dashboard-floor-plan-analytics/components/sticky-note/sticky-note.component";
import "../zones.scss";

type AnnotatedAsset = FloorShape & {
  assetId?: number | string;
  id?: number | string;
};

type SelectionState = {
  zone?: Zone | null;
  assignedAssets?: ZoneAsset[];
  returnPath?: string;
};

type ZoneFloorplanSelectionProps = {
  floorAssets: AnnotatedAsset[];
  floorAssetsLoaded: boolean;
  assignedAssets: ZoneAsset[];
  selectedAssetCodes: string[];
  onSelectionChange: (assetCodes: string[]) => void;
  onAssetHover: (assetCode: string) => void;
  onAssetHoverEnd: () => void;
};

export const isZoneSelectableAssetType = (assetType?: string) => {
  const normalizedType = assetType?.toLowerCase();
  return normalizedType !== "gateway" && normalizedType !== "photocell sensor";
};

export function ZoneFloorplanSelection({
  floorAssets,
  floorAssetsLoaded,
  assignedAssets,
  selectedAssetCodes,
  onSelectionChange,
  onAssetHover,
  onAssetHoverEnd,
}: ZoneFloorplanSelectionProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, blockCode: encodedBlockCode, floorCode = "" } = useParams<{
    id: string;
    blockCode: string;
    floorCode: string;
  }>();
  const selectionState = (location.state || {}) as SelectionState;
  const returnPath = selectionState.returnPath || `/asset/${id}/configuration/zones`;
  const initializedFloor = useRef<string>();
  const [isSelectedListVisible, setIsSelectedListVisible] = useState(false);
  const [stickyNotePosition, setStickyNotePosition] = useState(() => ({
    x: Math.max(12, window.innerWidth - 410),
    y: 110,
  }));

  const selectableFloorAssets = useMemo(
    () => floorAssets.filter((asset) => !!asset.assetCode && isZoneSelectableAssetType(asset.assetType)),
    [floorAssets],
  );

  const annotatedAssetCodes = useMemo(
    () => new Set(selectableFloorAssets.map((asset) => asset.assetCode)),
    [selectableFloorAssets],
  );

  const selectedAssets = useMemo(
    () => selectableFloorAssets.filter((asset) => selectedAssetCodes.includes(asset.assetCode)),
    [selectableFloorAssets, selectedAssetCodes],
  );

  const selectedAssetOrder = useMemo(
    () => Object.fromEntries(selectedAssetCodes.map((assetCode, index) => [assetCode, index + 1])),
    [selectedAssetCodes],
  );

  useEffect(() => {
    if (!floorAssetsLoaded) return;

    const floorKey = `${encodedBlockCode || ""}/${floorCode}`;
    if (initializedFloor.current === floorKey) return;
    initializedFloor.current = floorKey;

    const matchingAssetCodes = assignedAssets
      .filter((asset) => !asset.isRemove && annotatedAssetCodes.has(asset.assetCode))
      .map((asset) => asset.assetCode);
    onSelectionChange(matchingAssetCodes);
    setIsSelectedListVisible(matchingAssetCodes.length > 0);
  }, [
    annotatedAssetCodes,
    assignedAssets,
    encodedBlockCode,
    floorAssetsLoaded,
    floorCode,
    onSelectionChange,
  ]);

  const toggleAsset = (assetCode: string) => {
    if (!annotatedAssetCodes.has(assetCode)) return;
    if (selectedAssetCodes.includes(assetCode)) {
      onSelectionChange(selectedAssetCodes.filter((code) => code !== assetCode));
      return;
    }

    setIsSelectedListVisible(true);
    onSelectionChange([...selectedAssetCodes, assetCode]);
  };

  const handleConfirm = () => {
    const existingAssetsByCode = new Map(assignedAssets.map((asset) => [asset.assetCode, asset]));
    const selectedAssetCodeSet = new Set(selectedAssetCodes);
    const updatedAssignments = assignedAssets
      .filter((asset) =>
        selectionState.zone ||
        !annotatedAssetCodes.has(asset.assetCode) ||
        selectedAssetCodeSet.has(asset.assetCode),
      )
      .map((asset) => {
        if (!selectionState.zone || !annotatedAssetCodes.has(asset.assetCode)) return asset;
        return { ...asset, isRemove: !selectedAssetCodeSet.has(asset.assetCode) };
      });
    const newAssignments = selectableFloorAssets
      .filter((asset) =>
        selectedAssetCodeSet.has(asset.assetCode) && !existingAssetsByCode.has(asset.assetCode),
      )
      .map<ZoneAsset>((asset) => ({
        assetId: asset.assetId ?? asset.id,
        assetCode: asset.assetCode,
        assetName: asset.sensorDisplayName || asset.assetCode,
        assetType: asset.assetType,
        zoneTypeId: ZoneTypeId.LightNode,
      }));

    navigate(returnPath, {
      state: {
        zoneFloorSelection: {
          zone: selectionState.zone || null,
          selectedAssets: [...updatedAssignments, ...newAssignments],
        },
      },
    });
  };

  const handleBack = () => {
    navigate(returnPath, {
      state: {
        zoneFloorBack: {
          zone: selectionState.zone || null,
          assignedAssets,
        },
      },
    });
  };

  return (
    <>
      <div className="zone-floorplan-selection__analytics-header">
        <button
          type="button"
          className="zone-floorplan-selection__back"
          onClick={handleBack}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
          Back to Zones
        </button>
        <div className="zone-floorplan-selection__divider" />
        <div className="zone-floorplan-selection__zone-badge">
          <FontAwesomeIcon icon={faBuilding} className="zone-floorplan-selection__zone-icon" />
          {selectionState.zone?.name && (
            <>
              <span className="zone-floorplan-selection__zone-name">Zone: {selectionState.zone.name}</span>
              <span className="zone-floorplan-selection__zone-separator">·</span>
            </>
          )}
          <span className="zone-floorplan-selection__floor-name">
            Level: {selectionState.zone?.floorName || floorCode}
          </span>
        </div>
        {!isSelectedListVisible && (
          <B2Button
            variant="outline-secondary"
            className="d-block cursor-pointer analytics-header__button zone-floorplan-selection__restore-button"
            onClick={() => setIsSelectedListVisible(true)}
            title="Show Selected Assets"
          >
            <FontAwesomeIcon icon={faList} className="me-2" />
            Selected ({selectedAssets.length})
          </B2Button>
        )}
      </div>
      <div className="zone-floorplan-selection__note-layer">
        <StickyNoteComponent
          selectedAssets={selectedAssets.map((asset) => asset.assetCode)}
          selectedAssetOrder={selectedAssetOrder}
          isVisible={isSelectedListVisible}
          position={stickyNotePosition}
          onPositionChange={setStickyNotePosition}
          onRemoveAsset={(assetCode) => {
            onSelectionChange(selectedAssetCodes.filter((code) => code !== assetCode))
            onAssetHoverEnd();
          }}
          onClearAll={() => {
            onSelectionChange([]);
            onAssetHoverEnd();
          }}
          onToggleVisibility={() => setIsSelectedListVisible((visible) => !visible)}
          onAssetHover={onAssetHover}
          onAssetHoverEnd={onAssetHoverEnd}
          onConfirmSelection={handleConfirm}
          confirmDisabled={!floorAssetsLoaded || selectableFloorAssets.length === 0}
          showRestoreButton={false}
        />
      </div>
    </>
  );
}
